import React from 'react';
import PropTypes from 'prop-types';
import InteractionOutcomeIndicator from '../../interactionOutcome/InteractionOutcomeIndicator';
import constants from '../../../../qti/Constants';

import styles from './graphicGapMatchInteraction.scss';
import ImageDisplay from '../../media/image/ImageDisplay';
import interactionOutcomeUtils from '../../../../qti/utils/InteractionOutcomeUtils';
import LoadTracker from '../../../../services/LoadTracker';
import DragData from './DragData';
import sessionReader from '../../../../store/readHelpers/SessionReader';
import MathDisplay from '../../mathDisplay/MathDisplay';

const keys = constants.keyCodes;
const toBeFocusedOnNextMounting = {};

class GapChoice extends React.Component {
    static propTypes = {
        correctResponses: PropTypes.array,
        eventTracker: PropTypes.object.isRequired,
        gapChoiceModel: PropTypes.object.isRequired,
        handleGapChoiceArrowKeyUp: PropTypes.func.isRequired,
        handleGapChoiceDeleteKeyUp: PropTypes.func.isRequired,
        handleGapChoiceDragStart: PropTypes.func.isRequired,
        handleGapChoiceDragEnd: PropTypes.func.isRequired,
        hidden: PropTypes.bool,
        hotspotModel: PropTypes.object,
        isDropped: PropTypes.bool,
        isReadOnly: PropTypes.bool,
        isReusable: PropTypes.bool,
        reCalculatedGapChoiceDimensions: PropTypes.object,
        reportDimensions: PropTypes.func,
        reportReady: PropTypes.func,
        services: PropTypes.object.isRequired,
        shouldResizeToHotspot: PropTypes.bool,
        index: PropTypes.number,
    };

    static defaultProps = {
        reportReady: () => undefined,
    };

    constructor(props) {
        const { gapChoiceModel } = props;
        const { identifier } = gapChoiceModel;
        super(props);
        this.state = { focused: false };

        this.readyTracker = new LoadTracker(this.handleComponentReady, `GapChoice ${identifier}`);

        this.reportMounted = this.readyTracker.addItemToTrack(`GapChoice ${identifier} > mounted`);
        if (this.shouldShowImage(gapChoiceModel)) {
            this.reportImageReady = this.readyTracker.addItemToTrack(`GapChoice ${identifier} > image`);
        }
        if (this.shouldShowText(gapChoiceModel)) {
            this.reportTextReady = this.readyTracker.addItemToTrack(`GapChoice ${identifier} > text`);
        }
        if (this.shouldShowOutcomeIndicator(props)) {
            this.reportIconReady = this.readyTracker.addItemToTrack(`GapChoice ${identifier} > outcomeIcon`);
        }
        this.readyTracker.setAllItemsAdded();

        this.handleTextElementRef = this.handleTextElementRef.bind(this);
        this.handleKeyDown = this.handleKeyDown.bind(this);
        this.handleKeyUp = this.handleKeyUp.bind(this);
        window.addEventListener('keydown', this.handleKeyDown);
        window.addEventListener('keyup', this.handleKeyUp);
    }

    componentDidMount() {
        if (toBeFocusedOnNextMounting[this.props.gapChoiceModel.identifier]) {
            this.rootElement.focus();
            delete toBeFocusedOnNextMounting[this.props.gapChoiceModel.identifier];
        }

        this.reportMounted(true);
    }

    componentWillUnmount() {
        this.readyTracker.kill();
        window.removeEventListener('keydown', this.handleKeyDown);
        window.removeEventListener('keyup', this.handleKeyUp);
    }

    getDimensions = () => {
        try {
            const displayValue = this.rootElement.style.display;
            this.rootElement.style.display = 'inline-flex';
            const dimensions = { width: this.rootElement.clientWidth, height: this.rootElement.clientHeight };
            this.rootElement.style.display = displayValue;
            return dimensions;
        } catch (error) {
            return undefined;
        }
    };

    handleComponentReady = (success) => {
        if (this.props.reportDimensions) {
            this.props.reportDimensions(this.getDimensions());
        }
        this.props.reportReady(success);
    };

    handleDragStart = (e) => {
        const { eventTracker, gapChoiceModel, hotspotModel, isReusable } = this.props;
        const hotspotIdentifier = hotspotModel ? hotspotModel.identifier : undefined;

        this.rootElement.classList.add('dragging');
        if (!isReusable) {
            this.rootElement.classList.add('opacity');
        }
        eventTracker.track(e);
        DragData.setOnEvent(e, gapChoiceModel.identifier, hotspotIdentifier);
        this.props.handleGapChoiceDragStart();
    };

    handleDragEnd = (e) => {
        const { eventTracker, gapChoiceModel, hotspotModel } = this.props;
        const hotspotIdentifier = hotspotModel ? hotspotModel.identifier : undefined;
        const elementFromPoint = document.elementFromPoint(e.clientX, e.clientY);
        const droppedOnSelf = elementFromPoint === this.rootElement || elementFromPoint === this.rootElement.parentElement;

        this.rootElement.classList.remove('dragging', 'opacity');
        eventTracker.forget(e);
        this.props.handleGapChoiceDragEnd(gapChoiceModel.identifier, hotspotIdentifier, droppedOnSelf);
    };

    handleArrowKeyUp = (e) => {
        const { gapChoiceModel, hotspotModel } = this.props;
        const hotspotIdentifier = hotspotModel ? hotspotModel.identifier : undefined;
        const direction = e.keyCode < keys.RIGHT ? 'up' : 'down';

        toBeFocusedOnNextMounting[gapChoiceModel.identifier] = true;
        this.props.handleGapChoiceArrowKeyUp(gapChoiceModel.identifier, hotspotIdentifier, direction);

        return false;
    };

    handleKeyUp = (e) => {
        if (!this.state.focused) {
            return true;
        }

        if (e.keyCode >= keys.LEFT && e.keyCode <= keys.DOWN) {
            e.preventDefault();
            return this.handleArrowKeyUp(e);
        }

        const { gapChoiceModel, hotspotModel } = this.props;
        if ((e.keyCode === keys.DELETE || e.keyCode === keys.BACKSPACE) && this.props.handleGapChoiceDeleteKeyUp) {
            this.props.handleGapChoiceDeleteKeyUp(gapChoiceModel.identifier, hotspotModel.identifier);
        }
        return true;
    };

    handleKeyDown = (e) => {
        if (!this.state.focused) {
            return true;
        }

        if (e.keyCode >= keys.LEFT && e.keyCode <= keys.DOWN) {
            // We deal with these onKeyUp.
            e.preventDefault();
            return false;
        }

        return true;
    };

    handleBlur = () => this.setState({ focused: false });
    handleOnFocus = () => this.setState({ focused: true });

    getAnswerOption = () => {
        const { gapChoiceModel, hotspotModel } = this.props;
        if (hotspotModel) {
            return `${gapChoiceModel.identifier} ${hotspotModel.identifier}`;
        }
        return '';
    };

    async handleTextElementRef(element) {
        if (element === null) {
            return;
        }
        await MathDisplay.showMath(element);
        this.reportTextReady(true);
    }

    shouldShowImage(gapChoiceModel) {
        const { mediaModel } = gapChoiceModel;
        return mediaModel && mediaModel.type && mediaModel.type.includes('image');
    }

    shouldShowText(gapChoiceModel) {
        return gapChoiceModel.textContent.length > 0;
    }

    shouldShowOutcomeIndicator(props) {
        return props.correctResponses !== undefined;
    }

    render() {
        const {
            hotspotModel,
            gapChoiceModel,
            hidden,
            isDropped,
            isReadOnly,
            correctResponses,
            services,
            reCalculatedGapChoiceDimensions,
            shouldResizeToHotspot,
            index,
        } = this.props;
        const answerOption = this.getAnswerOption();
        const tabIndex = isReadOnly ? undefined : '0';
        const draggable = !isReadOnly && !sessionReader.isPrintStylePaperBased();
        const onFocus = isReadOnly ? undefined : this.handleOnFocus;

        const style = { visibility: hidden ? 'hidden' : 'visible' };

        let outcomeClass = '';
        if (correctResponses) {
            outcomeClass = interactionOutcomeUtils.isAnswerCorrect(answerOption, correctResponses)
                ? 'correct'
                : 'incorrect';
        }

        let height;
        let width;
        if (shouldResizeToHotspot) {
            height = hotspotModel.height;
            width = hotspotModel.width;
        } else if (reCalculatedGapChoiceDimensions !== undefined) {
            height = reCalculatedGapChoiceDimensions.height;
            width = reCalculatedGapChoiceDimensions.width;
        } else if (gapChoiceModel.dimensions) {
            height = gapChoiceModel.dimensions.height;
            width = gapChoiceModel.dimensions.width;
        }

        if (height && width) {
            style.height = `${height + 2}px`; // +2 matches the margin of -1px
            style.width = `${width + 2}px`; // +2 matches the margin of -1px
        }

        const classes = [styles.gapChoice, 'draggable', 'gapChoice', outcomeClass];
        if (sessionReader.isPrintStylePaperBased()) {
            classes.push('paper-based-token');
        }

        return (
            <div
                className={classes.join(' ')}
                data-identifier={gapChoiceModel.identifier}
                draggable={draggable}
                onBlur={this.handleBlur}
                onDragStart={this.handleDragStart}
                onDragEnd={this.handleDragEnd}
                onFocus={onFocus}
                style={style}
                tabIndex={tabIndex}
                ref={(element) => { this.rootElement = element; }}
            >
                {
                    sessionReader.isPrintStylePaperBased()
                        ? <span className="numbering">{services.utilService.toLatinLetter(index + 1)}</span>
                        : null
                }
                {
                    this.shouldShowImage(gapChoiceModel) ?
                        <ImageDisplay
                            model={gapChoiceModel.mediaModel}
                            reportReady={this.reportImageReady}
                            useModelWidthAndHeight
                        /> : null
                }
                {
                    this.shouldShowText(gapChoiceModel) ?
                        <div
                            className={styles.textNode}
                            dangerouslySetInnerHTML={{ __html: gapChoiceModel.textContent }}
                            ref={this.handleTextElementRef}
                        /> : null
                }
                {
                    this.shouldShowOutcomeIndicator(this.props) ?
                        <InteractionOutcomeIndicator
                            answerOption={answerOption}
                            isAttempted={isDropped}
                            correctResponses={correctResponses}
                            translationService={services.translationService}
                            onReady={this.reportIconReady}
                        /> : null
                }
            </div>
        );
    }
}

export default GapChoice;
